import {
  fetchMyProfile,
  getSession,
  isAllowedAvatarFile,
  onAuthStateChange,
  PROFILE_SOCIAL_LINK_KEYS,
  type ProfileSocialLinkKey,
  type ProfileSocialLinks,
  removeAvatarObject,
  updateMyProfile,
  uploadAvatar,
} from '@bangle.io/supabase';
import { Button, Input, toast } from '@bangle.io/ui-components';
import React from 'react';

const SOCIAL_LABELS: Record<ProfileSocialLinkKey, () => string> = {
  website: () => t.app.settings.account.socialWebsite,
  github: () => t.app.settings.account.socialGithub,
  mastodon: () => t.app.settings.account.socialMastodon,
  bluesky: () => t.app.settings.account.socialBluesky,
  youtube: () => t.app.settings.account.socialYoutube,
  email: () => t.app.settings.account.socialEmail,
};

export function AccountSettingsPage() {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [signedIn, setSignedIn] = React.useState(false);
  const [displayName, setDisplayName] = React.useState('');
  const [avatarUrl, setAvatarUrl] = React.useState('');
  const [initialAvatarUrl, setInitialAvatarUrl] = React.useState('');
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = React.useState<
    string | null
  >(null);
  const [showLinkInput, setShowLinkInput] = React.useState(false);
  const [bio, setBio] = React.useState('');
  const [socialLinks, setSocialLinks] = React.useState<ProfileSocialLinks>({});
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    return () => {
      if (pendingPreviewUrl) {
        URL.revokeObjectURL(pendingPreviewUrl);
      }
    };
  }, [pendingPreviewUrl]);

  React.useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const session = await getSession();
        if (cancelled) return;
        if (!session?.user) {
          setSignedIn(false);
          setLoading(false);
          return;
        }
        setSignedIn(true);
        const profile = await fetchMyProfile();
        if (cancelled) return;
        if (profile) {
          const nextAvatar = profile.avatar_url ?? '';
          setDisplayName(profile.display_name ?? '');
          setAvatarUrl(nextAvatar);
          setInitialAvatarUrl(nextAvatar);
          setBio(profile.bio ?? '');
          setSocialLinks(profile.social_links ?? {});
          setPendingFile(null);
          setPendingPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
          });
        }
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    };

    void loadProfile();
    const unsubscribe = onAuthStateChange((session) => {
      if (cancelled) return;
      if (!session?.user) {
        setSignedIn(false);
        setLoading(false);
        return;
      }
      void loadProfile();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const clearPendingFile = () => {
    setPendingFile(null);
    setPendingPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onPickFile = (file: File | undefined) => {
    if (!file) return;
    const check = isAllowedAvatarFile(file);
    if (!check.ok) {
      const message =
        check.reason === 'type'
          ? t.app.settings.account.avatarInvalidType
          : t.app.settings.account.avatarTooLarge;
      setError(message);
      toast.error(message);
      return;
    }
    setError(null);
    setPendingPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setPendingFile(file);
    setShowLinkInput(false);
  };

  const onRemoveAvatar = () => {
    clearPendingFile();
    setAvatarUrl('');
    setShowLinkInput(false);
  };

  const previewSrc = pendingPreviewUrl || avatarUrl || null;
  const initial = (displayName || '?').trim().slice(0, 1).toUpperCase() || '?';

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      let nextAvatarUrl = avatarUrl.trim() || null;
      const hadPendingUpload = Boolean(pendingFile);
      if (pendingFile) {
        try {
          nextAvatarUrl = await uploadAvatar(pendingFile);
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          setError(message);
          toast.error(t.app.toasts.avatarUploadFailed);
          return;
        }
      }

      await updateMyProfile({
        display_name: displayName,
        avatar_url: nextAvatarUrl,
        bio,
        social_links: socialLinks,
      });

      // Uploads upsert the same storage path — only delete when clearing or
      // switching to an external link.
      if (
        !hadPendingUpload &&
        initialAvatarUrl &&
        initialAvatarUrl !== (nextAvatarUrl ?? '')
      ) {
        try {
          await removeAvatarObject(initialAvatarUrl);
        } catch {
          // Best-effort cleanup of the previous uploaded object.
        }
      }

      setAvatarUrl(nextAvatarUrl ?? '');
      setInitialAvatarUrl(nextAvatarUrl ?? '');
      clearPendingFile();
      toast.success(t.app.toasts.profileSaved);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast.error(t.app.toasts.profileSaveFailed);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <p className="text-muted-foreground text-sm">
        {t.app.settings.account.loading}
      </p>
    );
  }

  if (!signedIn) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-muted-foreground text-sm">
          {t.app.settings.account.signInRequired}
        </p>
        {error ? (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="flex max-w-xl flex-col gap-6"
      data-testid="account-settings"
    >
      <section className="flex flex-col gap-3">
        <h3 className="font-medium text-sm">
          {t.app.settings.account.profileSection}
        </h3>
        <div className="flex flex-col gap-1.5">
          <span
            id="account-display-name-label"
            className="text-muted-foreground text-xs"
          >
            {t.app.settings.account.displayName}
          </span>
          <Input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            aria-labelledby="account-display-name-label"
          />
        </div>

        <div className="flex flex-col gap-2">
          <span
            id="account-avatar-label"
            className="text-muted-foreground text-xs"
          >
            {t.app.settings.account.avatar}
          </span>
          <div className="flex flex-wrap items-center gap-3">
            <div
              className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-input bg-muted"
              data-testid="account-avatar-preview"
            >
              {previewSrc ? (
                <img
                  src={previewSrc}
                  alt=""
                  className="size-full object-cover"
                />
              ) : (
                <span className="font-medium text-lg text-muted-foreground">
                  {initial}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="sr-only"
                onChange={(event) => {
                  onPickFile(event.target.files?.[0]);
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={saving}
                onClick={() => fileInputRef.current?.click()}
              >
                {t.app.settings.account.avatarUpload}
              </Button>
              {previewSrc ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={saving}
                  onClick={onRemoveAvatar}
                >
                  {t.app.settings.account.avatarRemove}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={saving}
                onClick={() => setShowLinkInput((prev) => !prev)}
              >
                {showLinkInput
                  ? t.app.settings.account.avatarHideLink
                  : t.app.settings.account.avatarUseLink}
              </Button>
            </div>
          </div>
          {showLinkInput ? (
            <Input
              value={pendingFile ? '' : avatarUrl}
              onChange={(event) => {
                clearPendingFile();
                setAvatarUrl(event.target.value);
              }}
              aria-label={t.app.settings.account.avatarUseLink}
              placeholder={t.app.settings.account.avatarLinkPlaceholder}
            />
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <span
            id="account-bio-label"
            className="text-muted-foreground text-xs"
          >
            {t.app.settings.account.bio}
          </span>
          <textarea
            className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={bio}
            onChange={(event) => setBio(event.target.value)}
            aria-labelledby="account-bio-label"
            rows={4}
          />
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="font-medium text-sm">
          {t.app.settings.account.socialSection}
        </h3>
        {PROFILE_SOCIAL_LINK_KEYS.map((key) => {
          const labelId = `account-social-${key}-label`;
          return (
            <div key={key} className="flex flex-col gap-1.5">
              <span id={labelId} className="text-muted-foreground text-xs">
                {SOCIAL_LABELS[key]()}
              </span>
              <Input
                value={socialLinks[key] ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  setSocialLinks((prev) => {
                    const next = { ...prev };
                    if (!value.trim()) {
                      delete next[key];
                    } else {
                      next[key] = value;
                    }
                    return next;
                  });
                }}
                aria-labelledby={labelId}
              />
            </div>
          );
        })}
      </section>

      {error ? (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      ) : null}

      <Button type="button" onClick={() => void onSave()} disabled={saving}>
        {saving ? t.app.settings.account.saving : t.app.settings.account.save}
      </Button>
    </div>
  );
}
